<template>

  <DefaultCrudView
    @create-new="newAction"
    @navigate-to-default-icons="navigateToDefaultIcons"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="actions"
        class="elevation-1"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:item.default_icon_id="{ item }">
          <v-icon v-if="item.default_icon_id">
            mdi-{{ getIconName(item.default_icon_id) }}
          </v-icon>
          <span v-else>-</span>
        </template>

        <template v-slot:item.tenant_id="{ item }">
          {{ getTenantTitle(item.tenant_id) }}
        </template>

        <template v-slot:item.actions="{ item }">

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="editAction(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip left v-if="canDeleteAction(item)">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click.stop="deleteAction(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>

        </template>

      </v-data-table>

      <DefaultModal 
        :title="getModalTitle()" 
        :hideSaveButton="shouldHideSaveButton"
        ref="modal" 
        v-on:submit="save"
      >
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="confirmDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">{{ $t('confirm') }}</v-card-title>
          <v-card-text>
            {{ $t('confirm_delete') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="confirmDialog = false">
              {{ $t('close') }}
            </v-btn>
            <v-btn color="red darken-1" text @click="confirmDelete">
              {{ $t('delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </template>
  
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }
</style>

<script>

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'

import ActionActions from '@/store/operation/action/actions-types'
import ActionMutations from '@/store/operation/action/mutations-types'
import AppActions from '@/store/app/actions-types'
import TenantActions from '@/store/core/tenant/actions-types'
import UserRoles from '@/store/core/user/user-roles'
import api from '@/services/api'

export default {
  name: "Actions",

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent
  },

  computed: {
    ...mapGetters('app', [
      'getIconForOperationSettingsItem'
    ]),

    ...mapGetters('core/auth', [
      'checkAccessRights'
    ]),

    ...mapState({
      fields: state => state.operation.action.fields,
      headers: state => state.operation.action.tableHeaders,
      selectedAction: state => state.operation.action.selectedAction
    }),

    breadcrumbs() {
      return [
        {
          text: this.$t('actions'),
          prependIcon: this.getIconForOperationSettingsItem('actions'),
        },
      ]
    },

    shouldHideSaveButton() {
      if (!this.selectedAction) {
        return false
      }

      const action = this.selectedAction
      const userIsMaster = this.isMaster
      const actionHasTenant = action.tenant_id !== null && action.tenant_id !== undefined

      if (userIsMaster && actionHasTenant) {
        return true
      }
      
      if (!userIsMaster && !actionHasTenant) {
        return true
      }

      return false
    }
  },

  data() {
    return {
      loading: true,
      actions: [],
      options: {
        sortBy: ['action_title'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [],
      tenants: [],
      defaultIcons: [],
      confirmDialog: false,
      actionToDelete: null,
      isMaster: false
    }
  },

  mounted() {
    this[ActionMutations.SET_SELECTED_ACTION](null)

    this.isMaster = this.checkUserIsMaster()

    // Set crud actions based on user role
    this.crudActions = []
    
    // Only master users can access default icons
    if (this.isMaster) {
      this.crudActions.push({
        text: "default_icons",
        eventToEmit: "navigate-to-default-icons",
      })
    }
    
    // All users can create actions
    this.crudActions.push({
      text: "create_action",
      eventToEmit: "create-new",
    })

    // Load tenants
    this[TenantActions.GET_ALL_TENANTS]()
      .then((tenants) => {
        this[ActionMutations.SET_FIELD_TENANTS](tenants.items)
        this.tenants = tenants.items
      })

    // Load default icons
    api.node.defaulticon.getDefaulticons()
      .then((response) => {
        this.defaultIcons = response.data.items || response.data
      })
      .catch(() => {
        console.warn('Failed to load default icons')
      })
  },

  watch: {
    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('operation/action', [
      ActionActions.GET_ALL_ACTIONS,
      ActionActions.SAVE_ACTION,
      ActionActions.UPDATE_ACTION,
      ActionActions.DELETE_ACTION,
    ]),

    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    ...mapMutations('operation/action', [
      ActionMutations.SET_SELECTED_ACTION,
      ActionMutations.SET_FIELD_TENANTS,
      ActionMutations.SET_TENANT_PERMISSIONS,
    ]),

    newAction() {
      this[ActionMutations.SET_SELECTED_ACTION](null)
      
      // Set tenant permissions based on user role
      this[ActionMutations.SET_TENANT_PERMISSIONS]({
        isMaster: this.isMaster,
        tenants: this.tenants
      })
      
      this.$refs.modal.openModal()
    },

    save() {
      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      this[ActionActions.SAVE_ACTION]()
        .then(() => {
          let message = this.selectedAction ? 'Action edited' : 'Action created'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch(() => this.$refs.modal.submitting = false)
    },

    reloadData() {
      let options = {...this.$route.query}

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)
      this.loading = true
      this[ActionActions.GET_ALL_ACTIONS](options)
        .then((result) => {
          // Handle different possible response structures
          this.actions = result.items || result.data || result || []
          this.itemsLength = result.total || result.count || (result.items ? result.items.length : 0)
          this.loading = false
        })
        .catch((error) => {
          console.error('Error loading actions:', error)
          this.loading = false
        })
    },

    editAction(action) {
      this[ActionMutations.SET_SELECTED_ACTION](action)
      
      // Set tenant permissions based on user role
      this[ActionMutations.SET_TENANT_PERMISSIONS]({
        isMaster: this.isMaster,
        tenants: this.tenants
      })
      
      this.$refs.modal.openModal()
    },

    deleteAction(action) {
      this.actionToDelete = action
      this.confirmDialog = true
    },

    confirmDelete() {
      if (this.actionToDelete) {
        this.$store.dispatch('operation/action/' + ActionActions.DELETE_ACTION, this.actionToDelete.action_id)
          .then(() => {
            this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Action deleted'))
            this.reloadData()
            this.confirmDialog = false
            this.actionToDelete = null
          })
          .catch((error) => {
            console.error('Error deleting action:', error)
            this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Error deleting action'))
            this.confirmDialog = false
            this.actionToDelete = null
          })
      }
    },

    getTenantTitle(id) {
      if (!id) return ''
      let tenant = this.tenants.find(f => f.tenant_id == id)

      return tenant ? tenant.title : ''
    },

    getIconName(iconId) {
      if (!iconId) return 'help-circle'
      
      const icon = this.defaultIcons.find(icon => icon.default_icon_id == iconId)
      if (icon && icon.icon_key) {
        // Remove 'mdi-' prefix if it exists since we add it in the template
        return icon.icon_key.replace(/^mdi-/, '')
      }
      
      return 'help-circle'
    },

    getModalTitle() {
      return this.selectedAction ? this.$t('edit_action') : this.$t('new_action')
    },

    checkUserIsMaster() {
      return this.checkAccessRights([UserRoles.USER_MASTER])
    },

    canDeleteAction(action) {
      if (!action) return false
      
      const actionHasTenant = action.tenant_id !== null && action.tenant_id !== undefined
      
      if (this.isMaster) {
        // Master can only delete actions without tenant
        return !actionHasTenant
      } else {
        // Admin can only delete actions with tenant
        return actionHasTenant
      }
    },

    navigateToDefaultIcons() {
      this.$router.push({ name: 'DefaultIcon' })
    }
  }
}

</script>
